import type { FC } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, ChevronLeft } from 'lucide-react'
import gsap from 'gsap'

import { EASE, prefersReducedMotion, registerGsap } from '@/lib/anim'
import { setScrollLocked } from '@/components/generals/smooth-scroll'
import { navLinks, studio } from '@/constants/dentea'

export const Navbar: FC = () => {
    const [open, setOpen] = useState(false)
    const overlay = useRef<HTMLDivElement>(null)
    const touched = useRef(false)
    const router = useRouter()

    const isHome = router.pathname === '/'

    /**
     * The nav pill and the logo mark share one entrance: down from above with a
     * slight scale-down, matching the original. The home page holds it back
     * until the hero bands have landed; inner pages bring it in right away.
     */
    const chromeEnter = {
        'data-enter': isHome ? '1.5' : '0.3',
        'data-enter-y': '-200',
        'data-enter-scale': '1.3'
    }

    /* Close the menu whenever navigation happens. */
    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    /* Escape closes it too. */
    useEffect(() => {
        if (!open) return

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    /* Open / close choreography. */
    useEffect(() => {
        const el = overlay.current
        if (!el) return

        // Nothing to play on mount: the overlay already renders closed.
        if (!open && !touched.current) return

        touched.current = true

        registerGsap()

        const panel = el.querySelector<HTMLElement>('[data-menu-panel]')
        const items = el.querySelectorAll<HTMLElement>('[data-menu-item]')
        const meta = el.querySelectorAll<HTMLElement>('[data-menu-meta]')

        if (!panel) return

        if (prefersReducedMotion()) {
            gsap.set(el, { autoAlpha: open ? 1 : 0 })
            gsap.set([items, meta], { opacity: 1, y: 0 })
            setScrollLocked(open)

            return
        }

        const tl = gsap.timeline()

        if (open) {
            setScrollLocked(true)

            tl.set(el, { autoAlpha: 1 })
                .fromTo(
                    panel,
                    { clipPath: 'inset(0% 0% 100% 0%)' },
                    { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.85, ease: EASE }
                )
                .fromTo(
                    items,
                    { opacity: 0, y: 46 },
                    { opacity: 1, y: 0, duration: 0.95, ease: EASE, stagger: 0.07 },
                    0.25
                )
                .fromTo(meta, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.8, ease: EASE, stagger: 0.06 }, 0.45)
        } else {
            tl.to(items, { opacity: 0, y: -24, duration: 0.35, ease: 'power2.in', stagger: 0.03 })
                .to(meta, { opacity: 0, duration: 0.3, ease: 'power2.in' }, 0)
                .to(panel, { clipPath: 'inset(0% 0% 100% 0%)', duration: 0.7, ease: EASE }, 0.2)
                .set(el, { autoAlpha: 0 })
                .call(() => setScrollLocked(false))
        }

        return () => {
            tl.kill()
        }
    }, [open])

    /* Never leave the page locked if the nav unmounts mid-animation. */
    useEffect(() => {
        return () => setScrollLocked(false)
    }, [])

    return (
        <>
            <header className='pointer-events-none fixed inset-x-0 top-0 z-50 flex items-start justify-between px-5 py-5 md:px-6'>
                <div className='pointer-events-auto w-10' />

                <nav
                    aria-label='Primary'
                    {...chromeEnter}
                    className='pointer-events-auto flex items-center gap-2 opacity-0'
                >
                    {!isHome && (
                        <button
                            type='button'
                            onClick={() => router.back()}
                            aria-label='Go back'
                            className='flex size-9 items-center justify-center rounded-full bg-ink text-paper transition-transform duration-500 ease-dentea hover:scale-105 active:scale-95'
                        >
                            <ChevronLeft aria-hidden='true' className='size-4' />
                        </button>
                    )}

                    <div className='flex items-center rounded-full border border-ink/10 bg-ink/6 p-1 backdrop-blur-md'>
                        <button
                            type='button'
                            onClick={() => setOpen(true)}
                            aria-expanded={open}
                            aria-haspopup='dialog'
                            className='label px-4 py-2 text-ink/70 transition-colors duration-300 hover:text-ink'
                        >
                            Menu
                        </button>

                        <Link
                            href='/contact'
                            className='label group/nav flex items-center gap-1.5 rounded-full bg-ink py-2 pr-3 pl-4 text-paper transition-colors duration-500 ease-dentea hover:bg-charcoal'
                        >
                            Start a Project
                            <ArrowRight
                                aria-hidden='true'
                                className='size-3.5 transition-transform duration-500 ease-dentea group-hover/nav:translate-x-1'
                            />
                        </Link>
                    </div>
                </nav>

                <Link
                    href='/'
                    aria-label={`${studio.name} home`}
                    {...chromeEnter}
                    className='group/mark pointer-events-auto mt-1.5 block w-8 shrink-0 opacity-0 md:w-9'
                >
                    <img
                        src={studio.mark}
                        alt=''
                        className='w-full mix-blend-multiply transition-opacity duration-300 group-hover/mark:opacity-60'
                    />
                </Link>
            </header>

            <div
                ref={overlay}
                role='dialog'
                aria-modal='true'
                aria-label='Site menu'
                className='invisible fixed inset-0 z-[65] opacity-0'
            >
                <div data-menu-panel className='flex h-full w-full flex-col bg-ink' style={{ clipPath: 'inset(0% 0% 100% 0%)' }}>
                    <div className='flex items-start justify-between px-5 py-5 md:px-6'>
                        <span data-menu-meta className='label text-paper/50'>
                            {studio.name}
                        </span>

                        <button
                            type='button'
                            onClick={() => setOpen(false)}
                            className='label rounded-full border border-paper/30 px-4 py-2 text-paper transition-colors duration-300 hover:bg-paper hover:text-ink'
                        >
                            Close
                        </button>
                    </div>

                    <nav aria-label='Menu' className='flex flex-1 flex-col justify-center px-5 md:px-10'>
                        <ul className='flex flex-col'>
                            {navLinks.map((link) => (
                                <li key={link.href} data-menu-item className='overflow-hidden'>
                                    <Link
                                        href={link.href}
                                        className='group/link flex items-baseline gap-4 border-b border-paper/10 py-3 md:gap-8 md:py-4'
                                    >
                                        <span className='label w-8 shrink-0 text-paper/40'>{link.index}</span>

                                        <span className='display block text-[13vw] text-paper transition-transform duration-700 ease-dentea group-hover/link:translate-x-3 md:text-[6.5vw]'>
                                            {link.label}
                                        </span>

                                        <ArrowRight
                                            aria-hidden='true'
                                            className='ml-auto size-5 shrink-0 self-center text-paper opacity-0 transition-all duration-500 ease-dentea group-hover/link:translate-x-0 group-hover/link:opacity-100 md:-translate-x-4'
                                        />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex flex-col gap-6 px-5 py-8 md:flex-row md:items-end md:justify-between md:px-10'>
                        <div data-menu-meta className='flex flex-col gap-1'>
                            <span className='label text-paper/40'>{studio.addressLabel}</span>

                            {studio.addressLines.map((line) => (
                                <span key={line} className='label text-paper'>
                                    {line}
                                </span>
                            ))}
                        </div>

                        <div data-menu-meta className='flex flex-col gap-1'>
                            <span className='label text-paper/40'>Contact</span>

                            <a href={studio.emailHref} className='label text-paper transition-opacity hover:opacity-60'>
                                {studio.email}
                            </a>

                            <a href={studio.phoneHref} className='label text-paper transition-opacity hover:opacity-60'>
                                {studio.phone}
                            </a>
                        </div>

                        <div data-menu-meta className='flex gap-5'>
                            {studio.social.map((item) => (
                                <a
                                    key={item.label}
                                    href={item.href}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='label text-paper/60 transition-colors hover:text-paper'
                                >
                                    {item.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
